import { ChangeDetectionStrategy, Component, forwardRef, inject, Input, Optional, SkipSelf } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective, FormsModule, NG_VALUE_ACCESSOR, NgForm } from '@angular/forms';
import { ArrayUtils } from '@core/utils';
import { TranslateModule } from '@ngx-translate/core';
import { BaseInput, controlContainerFactory } from '../base-input';
import { ErrorComponent } from '../error/error.component';
import { FieldsetComponent } from '../fieldset/fieldset.component';
import { SelectInputGroup } from '../types/select-input.types';

@Component({
    standalone: true,
    selector: 'ui-v2-select-input',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectInput),
            multi: true,
        },
        {
            provide: ControlContainer,
            useFactory: controlContainerFactory,
            deps: [
                [new Optional(), new SkipSelf(), FormGroupDirective],
                [new Optional(), new SkipSelf(), NgForm],
            ],
        },
    ],
    imports: [FormsModule, TranslateModule, ErrorComponent, FieldsetComponent],
    template: `
        <ui-v2-fieldset class="relative w-full" [legend]="legend" [label]="label">
            <select
                class="select w-full"
                [class.input-error]="showError"
                [(ngModel)]="innerModel"
                (ngModelChange)="onModelChange()"
                (blur)="onBlur()"
                [disabled]="innerDisabled"
            >
                @if (placeholder) {
                    <option disabled [ngValue]="null">{{ placeholder | translate }}</option>
                }
                @for (option of inputGroup?.options; track option.value) {
                    <option [ngValue]="option.value">{{ option.labelResource | translate }}</option>
                }
            </select>

            <ui-v2-input-error [showError]="showError" [error]="error"></ui-v2-input-error>
        </ui-v2-fieldset>
    `,
})
export class SelectInput<T> extends BaseInput<T> {
    @Input() formControl: FormControl;
    @Input() formControlName: string;
    @Input() legend: string;
    @Input() label: string;
    @Input() placeholder: string = 'V2.INPUT.SELECT.PLACEHOLDER';
    @Input() inputGroup: SelectInputGroup<T>;

    controlContainer = inject(ControlContainer);

    public readonly ArrayUtils = ArrayUtils;
    public isOpen = false;

    public onModelChange(): void {
        this.propagateChange(this.innerModel);
    }

    public writeValue(value: T): void {
        this.innerModel = value ?? null;
    }

    public toggleDropdown(open: boolean) {
        this.isOpen = open;
    }
}
